import React, { useEffect, useState, useRef, useMemo } from "react";
import { Typography, ConfigProvider, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { fetchManagers, deleteMerchant } from "../../redux/MerchantsUser";
import { useTheme } from "../../context/ThemeContext";
import { getAntdThemeConfig } from "../../utils/antdTheme";
import { ThemeProvider } from "styled-components";
import MerchantBox from "./MerchantBox";
import MerchantsTable from "./MerchantsTable";
import CustomInput from "../../commonComponents/input";
import { Merchant } from "./types";
import {
  Container,
  StyledCard,
  HeaderSection,
  TopControlBar,
  AddButton,
} from "./styles";

const { Title } = Typography;

const MerchantManager: React.FC<{ role?: string }> = ({ role }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [isEditing, setIsEditing] = useState(false);
  const [currentMerchant, setCurrentMerchant] = useState<Merchant | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  const dispatch = useDispatch<AppDispatch>();
  const { managers, loading } = useSelector(
    (state: RootState) => state.merchants
  );
  const hasFetchedRef = useRef(false);

  const filteredMerchants = useMemo(() => {
    if (!Array.isArray(managers)) return [];
    if (!searchQuery) return managers;
    const query = searchQuery.toLowerCase();
    return managers.filter(
      (merchant) =>
        merchant?.name?.toLowerCase().includes(query) ||
        merchant?.username?.toLowerCase().includes(query) ||
        merchant?.email?.toLowerCase().includes(query)
    );
  }, [managers, searchQuery]);

  const paginatedMerchants = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredMerchants.slice(startIndex, endIndex);
  }, [filteredMerchants, currentPage, pageSize]);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      dispatch(fetchManagers());
    }
  }, [dispatch]);

  const handleAddClick = () => {
    setCurrentMerchant(null);
    setIsEditing(true);
  };

  const handleEdit = (merchant: Merchant) => {
    setCurrentMerchant(merchant);
    setIsEditing(true);
  };

  const handleDelete = async (merchant: Merchant) => {
    const result = await dispatch(
      deleteMerchant({ type: "manager", id: merchant.id })
    );
    if (deleteMerchant.fulfilled.match(result)) {
      message.success("Merchant manager deleted successfully");
      dispatch(fetchManagers());
    } else {
      const errorMsg =
        typeof result.payload === "string"
          ? result.payload
          : "Error deleting merchant manager";
      message.error(errorMsg);
    }
  };

  if (role !== "merchant") {
    return (
      <Container>
        <StyledCard>
          <div>You don't have the permissions to access this page.</div>
        </StyledCard>
      </Container>
    );
  }

  return (
    <ThemeProvider theme={{ mode: isDarkMode ? "dark" : "light" }}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          {!isEditing ? (
            <>
              <HeaderSection>
                <Title level={2} style={{ margin: 0 }}>
                  Users
                </Title>
                <AddButton
                  type="primary"
                  icon={<FontAwesomeIcon icon={faPlus} />}
                  onClick={handleAddClick}
                  size="large"
                >
                  Add New User
                </AddButton>
              </HeaderSection>

              <StyledCard>
                <TopControlBar>
                  <CustomInput
                    placeholder="Search users by name..."
                    value={searchQuery}
                    onChange={(value: string) => setSearchQuery(value)}
                    allowClear
                    width="300px"
                  />
                </TopControlBar>

                <MerchantsTable
                  merchants={paginatedMerchants}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  total={filteredMerchants.length}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={setPageSize}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  loading={loading.managers}
                />
              </StyledCard>
            </>
          ) : (
            <StyledCard>
              <MerchantBox
                initialMerchant={currentMerchant}
                onSave={() => {
                  setIsEditing(false);
                  setCurrentMerchant(null);
                  dispatch(fetchManagers());
                }}
                onCancel={() => {
                  setIsEditing(false);
                  setCurrentMerchant(null);
                }}
                fetchMerchants={() => dispatch(fetchManagers())}
                isManagerMode={true}
              />
            </StyledCard>
          )}
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default MerchantManager;